import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// ######### MATERIAL UI
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import BlockIcon from "@material-ui/icons/Block";

// ######### CUSTOM COMPONENTS
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import SectionGroupTables from "./SectionGroupTables";
import SectionTables from "./SectionTables";
import SectionSetupKeys from "./SectionSetupKeys";

// ######### API CALLS
import {
  getSetupkeyMergeDefinition,
  getSetupkeyMapping,
} from "../../viewServices/apiRelationships";
import { getScenarioPremises } from "../../../Cockpit/viewServices/calls/apiCalls";

// ######### STYLES
const dialogStyles = (theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      width: "80%",
      maxWidth: "1100px",
      height: "80vh",
      maxHeight: "800px",
      borderRadius: "5px",
      display: "flex",
      flexDirection: "column",
      margin: "10px",
    },
  },
  dialogHeader: {
    flexShrink: 0,
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
  },
  dialogCard: {
    marginTop: 0,
    marginBottom: 0,
  },
  cardHeaderTitle: {
    ...theme.typography.h6,
    fontSize: "1.125rem",
    color: theme.palette.secondary.main,
    fontWeight: "400",
    marginBottom: "0px",
    marginTop: "4px",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  headerVariantsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    minHeight: "84px",
    padding: theme.spacing(1, 2),
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e0e0e0",
    alignItems: "center",
  },
  dialogContent: {
    flex: 1,
    overflow: "auto",
    padding: "16px",
    backgroundColor: "#ffffff",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    minHeight: "400px",
  },
  // Section 1: Tables
  sectionTitle: {
    fontSize: "0.875rem",
    flexBasis: "100%",
    fontWeight: "500",
    color: theme.palette.secondary.main,
    marginBottom: "0px",
    letterSpacing: "0.5px",
  },
  variantChip: {
    cursor: "pointer",
    minWidth: "100px",
    height: "32px",
    borderRadius: "16px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#ffffff",
    color: "#666666",
    fontSize: "0.8125rem",
    fontWeight: "400",
    "& .MuiChip-label": {
      whiteSpace: "normal",
      textAlign: "center",
      lineHeight: "1.2",
      paddingLeft: "12px",
      paddingRight: "12px",
    },
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#d0d0d0",
    },
    "&.selected": {
      backgroundColor: (props) =>
        props.userDetails?.implementation_color || "#1976d2",
      borderColor: (props) =>
        props.userDetails?.implementation_color || "#1976d2",
      color: "#ffffff",
      fontWeight: "500",
      "&:hover": {
        backgroundColor: (props) =>
          props.userDetails?.implementation_color || "#1565c0",
      },
    },
  },
  noVariantsMessage: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    padding: theme.spacing(2),
  },
  // 3-Column Layout
  threeColumnContainer: {
    display: "grid",
    gridTemplateColumns: "240px 280px 1fr",
    gap: theme.spacing(2),
    height: "90%",
    overflow: "hidden",
  },
  // Column 1: Groups
  groupsColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    overflowY: "auto",
    padding: theme.spacing(1),
    borderRight: "1px solid #e0e0e0",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "3px",
      "&:hover": {
        background: "#555",
      },
    },
  },
  groupButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#d0d0d0",
    },
    "&.selected": {
      backgroundColor: (props) =>
        `${props.userDetails?.implementation_color}10`,
      borderColor: (props) => props.userDetails?.implementation_color,
      borderLeftWidth: "4px",
    },
  },
  groupIcon: {
    fontSize: "1rem",
    opacity: 0.7,
    color: (props) => props.userDetails?.implementation_color || "#1976d2",
  },
  groupName: {
    fontSize: "0.8125rem",
    fontWeight: "500",
    color: (props) => props.userDetails?.implementation_color || "#1976d2",
  },
  clientLogo: {
    height: "18px",
    width: "auto",
    maxWidth: "80px",
    opacity: 0.6,
    filter: "grayscale(80%) brightness(1.1)",
    objectFit: "contain",
    display: "block",
  },
  // Column 2: Tables
  tablesColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    overflowY: "auto",
    marginLeft: "-12px",
    padding: theme.spacing(1),
    borderRight: "1px solid #e0e0e0",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "3px",
      "&:hover": {
        background: "#555",
      },
    },
  },
  tableButton: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#d0d0d0",
    },
    "&.selected": {
      backgroundColor: (props) => props.userDetails?.implementation_color,
      borderColor: (props) => props.userDetails?.implementation_color,
      color: "#ffffff",
      "& $tableName, & $tableDescription": {
        color: "#ffffff",
      },
    },
  },
  tableName: {
    fontSize: "0.8125rem",
    fontWeight: "500",
    color: theme.palette.secondary.main,
  },
  tableDescription: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: "1.3",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
  },
  // Column 3: Configuration
  configColumn: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
    backgroundColor: "#f9f9f9",
    borderRadius: "4px",
    overflow: "hidden",
  },
  configHeader: {
    fontSize: "0.875rem",
    fontWeight: "500",
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: "1px solid #e0e0e0",
  },
  configMock: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "50px",
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    fontStyle: "italic",
  },
});

const useStyles = makeStyles(dialogStyles);

export default function DialogRelationships({
  open,
  onClose,
  structuredMetadata,
  getIconForGroup,
  inputColumns,
  userDetails,
}) {
  const classes = useStyles({ userDetails });
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });

  // ===== ESTADOS =====
  const [scenarioPremises, setScenarioPremises] = useState([]);
  const [selectedPremise, setSelectedPremise] = useState(null);
  const [isLoadingPremises, setIsLoadingPremises] = useState(false);
  const [setupkeyMerges, setSetupkeyMerges] = useState({});
  const [setupKeyMappings, setSetupKeyMappings] = useState({}); // Formato com UUID
  const [isLoadingMappings, setIsLoadingMappings] = useState(false);

  // New states for 3-column layout
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);

  // (fetch of variants is handled in the effect that runs on `open`)

  // ===== DERIVE GROUPS FROM STRUCTURED METADATA (memoized) =====
  const groups = useMemo(() => {
    if (!structuredMetadata || Object.keys(structuredMetadata).length === 0)
      return [];
    const groupsMap = new Map();
    Object.entries(structuredMetadata).forEach(([level1, level2Groups]) => {
      Object.entries(level2Groups).forEach(([groupName, tables]) => {
        const filteredTables = tables.filter(
          (t) => (t.id || "").toLowerCase() !== "model01",
        );
        if (filteredTables.length === 0) return;
        if (!groupsMap.has(groupName)) {
          groupsMap.set(groupName, {
            name: groupName,
            level1: parseInt(level1),
            tables: [],
          });
        }
        groupsMap.get(groupName).tables.push(
          ...filteredTables.map((t) => ({
            id: t.id,
            name: t.name,
            description: t.desc,
            ready: t.ready,
          })),
        );
      });
    });
    return Array.from(groupsMap.values()).sort((a, b) => a.level1 - b.level1);
  }, [structuredMetadata]);

  // ===== DERIVE TABLES OF SELECTED GROUP (memoized) =====
  const tablesInGroup = useMemo(() => {
    const g = groups.find((g) => g.name === selectedGroup);
    return g ? g.tables : [];
  }, [groups, selectedGroup]);

  // ===== EFFECTS =====
  useEffect(() => {
    if (!open) return;
    (async () => {
      setIsLoadingPremises(true);
      try {
        // fetch scenario premises
        const premises = await getScenarioPremises();
        setScenarioPremises(premises || []);
        if (premises && premises.length > 0 && !selectedPremise) {
          setSelectedPremise(premises[0]);
        }

        // mount Setupkey Definition using Schema inputColumns
        const setupkeyMergesData = getSetupkeyMergeDefinition(inputColumns);
        setSetupkeyMerges(setupkeyMergesData);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setIsLoadingPremises(false);
      }
    })();
  }, [open, inputColumns]);

  // Reset when premise changes
  useEffect(() => {
    if (selectedPremise && groups.length > 0 && !selectedGroup) {
      setSelectedGroup(groups[0].name);
      setSelectedTable(null);
    }
  }, [selectedPremise, groups, selectedGroup]);

  // Function to load mappings (can be called to refresh)
  const loadMappings = useCallback(async () => {
    if (!selectedPremise?.premise_key) return;

    setIsLoadingMappings(true);
    try {
      const allMappings = await getSetupkeyMapping(selectedPremise.premise_key);

      if (!allMappings || Object.keys(allMappings).length === 0) {
        setSetupKeyMappings({});
        return;
      }

      // Transforma para formato com UUID (usado pelo SectionSetupKeys)
      const transformedWithUUID = {};
      Object.entries(allMappings).forEach(([sourceTable, setupKeys]) => {
        if (!setupKeys || typeof setupKeys !== "object") return;

        Object.entries(setupKeys).forEach(([setupKey, columns]) => {
          const compositeKey = `${sourceTable}__${setupKey}`;
          const columnsArray = Array.isArray(columns) ? columns : [];
          // Adiciona UUID para cada coluna
          transformedWithUUID[compositeKey] = columnsArray.map((colId) => ({
            id: crypto.randomUUID(),
            columnId: colId,
          }));
        });
      });

      setSetupKeyMappings(transformedWithUUID);
    } catch (error) {
      console.error("Failed to load mappings:", error);
      setSetupKeyMappings({});
    } finally {
      setIsLoadingMappings(false);
    }
  }, [selectedPremise]);

  // Load setupkey mappings when premise changes
  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  // Derive simple format (without UUID) for SectionTables status calculation
  const setupKeyMappingsSimple = useMemo(() => {
    const simple = {};
    Object.entries(setupKeyMappings).forEach(([compositeKey, entries]) => {
      simple[compositeKey] = Array.isArray(entries)
        ? entries.map((entry) => entry.columnId)
        : [];
    });
    return simple;
  }, [setupKeyMappings]);

  // ===== HANDLERS =====
  const handlePremiseChange = useCallback((premise) => {
    setSelectedPremise(premise);
  }, []);

  const handleGroupClick = useCallback((group) => {
    setSelectedGroup(group.name);
    setSelectedTable(null);
  }, []);

  const handleTableClick = useCallback((table) => {
    setSelectedTable(table.id);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedPremise(null);
    onClose();
  }, [onClose]);

  // ===== HELPER: GET TABLE DESCRIPTION =====
  const getTableDescription = useCallback(
    (tableId) => {
      if (!structuredMetadata) return tableId;
      for (const level1 of Object.values(structuredMetadata)) {
        for (const group of Object.values(level1)) {
          const foundTable = group.find((t) => t.id === tableId);
          if (foundTable) return foundTable.desc;
        }
      }
      return tableId;
    },
    [structuredMetadata],
  );

  // ===== RENDER ICON HELPER =====
  const renderIcon = useCallback(
    (groupName) => {
      return getIconForGroup(
        groupName,
        {
          columnIcon: classes.groupIcon,
          clientLogo: classes.clientLogo,
          topSectionIcon: classes.groupIcon,
        },
        userDetails,
      );
    },
    [getIconForGroup, classes.groupIcon, classes.clientLogo, userDetails],
  );

  // ===== RENDER =====
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      className={classes.dialog}
    >
      {/* HEADER */}
      <Box className={classes.dialogHeader}>
        <Card className={classes.dialogCard}>
          <CardHeader style={{ padding: "8px 16px", position: "relative" }}>
            <h4 className={classes.cardHeaderTitle}>
              {t("manage_relationships")}
            </h4>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </CardHeader>
        </Card>

        {/* PREMISES IN HEADER */}
        <Box className={classes.headerVariantsContainer}>
          <Typography className={classes.sectionTitle}>
            {t("select_premise")}
          </Typography>
          <Select
            value={selectedPremise?.premise_key || ""}
            onChange={(e) => {
              const premise = scenarioPremises.find(
                (p) => p.premise_key === e.target.value,
              );
              handlePremiseChange(premise);
            }}
            disabled={isLoadingPremises || scenarioPremises.length === 0}
            style={{ minWidth: 200 }}
          >
            {scenarioPremises.map((premise) => (
              <MenuItem key={premise.premise_key} value={premise.premise_key}>
                {premise.premise_label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* CONTENT */}
      <Box className={classes.dialogContent}>
        {selectedPremise ? (
          <>
            <Typography className={classes.sectionTitle}>
              {t("relationship_section_title")}
            </Typography>

            {/* Check if setup keys are disabled for this premise */}
            {!selectedPremise?.is_setupkey_generated ? (
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "80px 24px",
                  textAlign: "center",
                  flex: 1,
                }}
              >
                <BlockIcon
                  style={{
                    fontSize: "48px",
                    color: "#bbb",
                    marginBottom: "24px",
                    opacity: 0.6,
                  }}
                />
                <Typography
                  style={{
                    fontSize: "1rem",
                    fontWeight: "400",
                    color: "#757575",
                    letterSpacing: "0.3px",
                  }}
                >
                  {t("setupkey_generation_disabled")}
                </Typography>
              </Box>
            ) : (
              <Box className={classes.threeColumnContainer}>
                {/* COLUMN 1: GROUPS */}
                <SectionGroupTables
                  groups={groups}
                  selectedGroup={selectedGroup}
                  onSelectGroup={handleGroupClick}
                  renderIcon={renderIcon}
                  scenarioPremises={scenarioPremises}
                  currentPremise={selectedPremise}
                  onCopySuccess={loadMappings}
                  classes={{
                    groupsColumn: classes.groupsColumn,
                    groupButton: classes.groupButton,
                    groupIcon: classes.groupIcon,
                    groupName: classes.groupName,
                    clientLogo: classes.clientLogo,
                  }}
                />

                {/* COLUMN 2: TABLES IN SELECTED GROUP */}
                <SectionTables
                  tables={tablesInGroup}
                  selectedTable={selectedTable}
                  onSelectTable={handleTableClick}
                  setupkeyMerges={setupkeyMerges}
                  setupKeyMappings={setupKeyMappingsSimple}
                  isLoadingMappings={isLoadingMappings}
                  inputColumns={inputColumns}
                  clientColor={userDetails?.implementation_color || "#1976d2"}
                  classes={{
                    tablesColumn: classes.tablesColumn,
                    tableButton: classes.tableButton,
                    tableName: classes.tableName,
                    tableDescription: classes.tableDescription,
                  }}
                />

                {/* COLUMN 3: CONFIGURATION */}
                <SectionSetupKeys
                  selectedPremise={selectedPremise}
                  selectedTable={selectedTable}
                  setupkeyMerges={setupkeyMerges}
                  getTableDescription={getTableDescription}
                  inputColumns={inputColumns}
                  setupKeyMappings={setupKeyMappings}
                  isLoadingMappings={isLoadingMappings}
                  onMappingsChange={loadMappings}
                  classes={{
                    configColumn: classes.configColumn,
                    configHeader: classes.configHeader,
                    configMock: classes.configMock,
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Box className={classes.loadingBox}>
            <Typography className={classes.noVariantsMessage}>
              {t("select_variant_to_configure")}
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

DialogRelationships.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  structuredMetadata: PropTypes.object.isRequired,
  getIconForGroup: PropTypes.func.isRequired,
  inputColumns: PropTypes.object.isRequired,
  userDetails: PropTypes.object.isRequired,
};
